import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { styled } from '@material-ui/core/styles';
import {
  Box,
  IconButton,
  Popover,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  InputAdornment,
  IconButton as MuiIconButton
} from '@material-ui/core';
import {
  Person as PersonIcon,
  Menu as MenuIcon,
  Login as LoginIcon,
  HowToReg as SignUpIcon,
  CardGiftcard as GiftIcon,
  SportsTennis as CourtIcon,
  HomeMini as RegisterCourtIcon,
  Help as HelpIcon,
  Visibility,
  VisibilityOff
} from '@material-ui/icons';
import SecureLS from 'secure-ls';

const ls = new SecureLS({ encodingType: 'aes' });

const MenuItemStyle = styled(ListItem)(({ theme }) => ({
  padding: theme.spacing(1, 2.5),
  '&:hover': {
    backgroundColor: '#000',
    cursor: 'pointer'
  }
}));

const ProfileButtonStyle = styled(IconButton)(({ theme }) => ({
  border: '1px solid',
  borderColor: theme.palette.grey[100],
  borderRadius: 28,
  padding: '5px 12px',
  display: 'flex',
  backgroundColor: theme.palette.grey[100],
  alignItems: 'center',
  gap: theme.spacing(1),
  '&:hover': {
    backgroundColor: '#00F5FF',
    boxShadow: theme.shadows[1]
  }
}));

const ProfileMenuPopover = ({ user_api }) => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const [openLogin, setOpenLogin] = useState(false);
  const [openRegister, setOpenRegister] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  // Login form state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  
  // Register form state
  const [registerEmail, setRegisterEmail] = useState('');
  const [registerPassword, setRegisterPassword] = useState('');
  const [registerContact, setRegisterContact] = useState('');
  const [registerName, setRegisterName] = useState('');

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleShowPassword = () => {
    setShowPassword((show) => !show);
  };

  const handleSignIn = async () => {
    if (!email || !password) {
      alert('All fields are required');
      return;
    }
    
    const data = {
      email: email,
      password: password,
    };
    
    const result = await user_api.sign_in_email(data);
    if (result.status !== 200) {
      alert(result.data.msg);
    } else {
      ls.set("token", result.data.token);
      alert("Login Successful");
      setOpenLogin(false);
      handleClose();
    }
  };

  const handleSignUp = async () => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    
    if (!registerEmail || !registerPassword || !registerContact || !registerName) {
      alert('All fields are required');
      return;
    }
    
    if (!regex.test(registerEmail)) {
      alert("Invalid email address");
      return;
    }
    
    const data = {
      email: registerEmail,
      password: registerPassword,
      phone: registerContact,
      name: registerName
    };
    
    const result = await user_api.sign_up_email(data);
    if (result.status !== 200) {
      alert(result.data.msg);
    } else {
      // Close register dialog
      setOpenRegister(false);
      
      // Show success message with email verification info
      alert(
        "Registration Successful! Please check your email to verify your account. " +
        "You can log in now, but some features will be limited until you verify your email."
      );
      
      // Open login dialog after a short delay
      setTimeout(() => {
        setOpenLogin(true);
      }, 3000);
    }
  };

  let isAuthenticated = false;

  try {
    const token = ls.get("token");
    isAuthenticated = Boolean(token);
  } catch (err) {
    console.warn("⚠️ Invalid or corrupted token detected. Clearing it.", err);
    ls.remove("token");
  }


  const menuItems = isAuthenticated ? [
    { title: 'My Bookings', icon: <PersonIcon />, path: '/user/profile/bookings' },
    { title: 'Profile Settings', icon: <PersonIcon />, path: '/user/profile/settings' },
    { title: 'Register your court', icon: <RegisterCourtIcon />, path: 'register-court' },
    { divider: true },
    { title: 'Help Center', icon: <HelpIcon />, path: '/help' },
    { 
      title: 'Logout', 
      icon: <LoginIcon />, 
      onClick: () => {
        ls.remove("token");
        handleClose();
        navigate('/');
      }
    }
  ] : [
    { 
      title: 'Sign up', 
      icon: <SignUpIcon />, 
      onClick: () => {
        setOpenRegister(true);
        handleClose();
      }
    },
    { 
      title: 'Login', 
      icon: <LoginIcon />, 
      onClick: () => {
        setOpenLogin(true);
        handleClose();
      }
    },
    {
      title: 'Register your court',
      icon: <RegisterCourtIcon />,
      path: 'Register-court'
    },
    { divider: true },
    { title: 'Help Center', icon: <HelpIcon />, path: '/help' }
  ];

  return (
    <Box>
      <ProfileButtonStyle onClick={handleClick}>
        <MenuIcon sx={{ fontSize: 20 }} />
        <Divider orientation="vertical" flexItem sx={{ height: 24, mx: 1 }} />
        <PersonIcon sx={{ fontSize: 20 }} />
      </ProfileButtonStyle>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
        PaperProps={{
          sx: {
            mt: 1.5,
            width: 250,
            borderRadius: 2,
            boxShadow: (theme) => theme.customShadows.z24
          }
        }}
      >
        <List sx={{ py: 1 }}>
          {menuItems.map((item, index) => 
            item.divider ? (
              <Divider key={`divider-${index}`} sx={{ my: 1 }} />
            ) : (
              <MenuItemStyle
                key={item.title}
                onClick={item.onClick || (() => {
                  navigate(item.path);
                  handleClose();
                })}
              >
                <ListItemIcon sx={{ minWidth: 32 }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText 
                  primary={item.title}
                  primaryTypographyProps={{ variant: 'subtitle2' }}
                />
              </MenuItemStyle>
            )
          )}
        </List>
      </Popover>

      {/* Login Dialog */}
      <Dialog open={openLogin} onClose={() => setOpenLogin(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Login</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Email Address"
            type="email"
            fullWidth
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <TextField
            margin="dense"
            label="Password"
            type={showPassword ? 'text' : 'password'}
            fullWidth
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <MuiIconButton onClick={handleShowPassword} edge="end">
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </MuiIconButton>
                </InputAdornment>
              )
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenLogin(false)}>Cancel</Button>
          <Button onClick={handleSignIn} variant="contained" style={{ backgroundColor: "#ff9800", color: '#fff' }}>
            Sign In
          </Button>
        </DialogActions>
      </Dialog>

      {/* Register Dialog */}
      <Dialog open={openRegister} onClose={() => setOpenRegister(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Register</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Full Name"
            type="text"
            fullWidth
            value={registerName}
            onChange={(e) => setRegisterName(e.target.value)}
          />
          <TextField
            margin="dense"
            label="Email Address"
            type="email"
            fullWidth
            value={registerEmail}
            onChange={(e) => setRegisterEmail(e.target.value)}
          />
          <TextField
            margin="dense"
            label="Password"
            type={showPassword ? 'text' : 'password'}
            fullWidth
            value={registerPassword}
            onChange={(e) => setRegisterPassword(e.target.value)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <MuiIconButton onClick={handleShowPassword} edge="end">
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </MuiIconButton>
                </InputAdornment>
              )
            }}
          />
          <TextField
            margin="dense"
            label="Contact Number"
            type="text"
            fullWidth
            value={registerContact}
            onChange={(e) => setRegisterContact(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenRegister(false)}>Cancel</Button>
          <Button onClick={handleSignUp} variant="contained" style={{ backgroundColor: "#ff9800", color: '#fff' }}>
            Sign Up
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProfileMenuPopover;